import type { FramingBoundaryWithMetadata } from "building-types/framing/framingMetaDataTypes";
import type { SlopedTopPlateClipResult } from "building-types/framing/wallFramingTopPlateClipTypes";
import { WallFramingType } from "building-types/framing/wallFramingTypes";
import { DIST_EPSILON } from "geom/core";
import { planeByNormal } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polyLine2Edge } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { classifyBoundaryEdgeType, edgeIndicesAtVertex } from "./wallFramingUtils";

function isSlopedTopPlateEdge(edge: PolyLine2): boolean {
  const dx = edge[2] - edge[0];
  const dy = edge[3] - edge[1];
  return Math.abs(dx) > DIST_EPSILON && Math.abs(dy) > DIST_EPSILON;
}

function isPeakVertex(perimeter: PolyLine2, x: number, y: number): boolean {
  let topPlateEdgeCount = 0;
  for (const edgeIndex of edgeIndicesAtVertex(perimeter, x, y)) {
    if (classifyBoundaryEdgeType(perimeter, edgeIndex) === WallFramingType.TOP_PLATE) {
      topPlateEdgeCount++;
    }
  }
  return topPlateEdgeCount >= 2;
}

function isCornerVertex(perimeter: PolyLine2, x: number, y: number): boolean {
  for (const edgeIndex of edgeIndicesAtVertex(perimeter, x, y)) {
    if (classifyBoundaryEdgeType(perimeter, edgeIndex) === WallFramingType.STANDARD_STUD) {
      const edge = polyLine2Edge(perimeter, edgeIndex);
      const edx = edge[2] - edge[0];
      const edy = edge[3] - edge[1];
      if (Math.abs(edx) < Math.abs(edy) - DIST_EPSILON) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Vertical clip plane in layout X. clipFacesWithPlanes keeps BACK; normal +X keeps x <= origin.
 */
function verticalClipPlaneAtLayoutX(layoutX: number, keepHigherXSide: boolean, elevation: number): Plane {
  const nx = keepHigherXSide ? -1 : 1;
  return planeByNormal(layoutX, 0, elevation, nx, 0, 0);
}

function slopeExtensionAlongEdge(edge: PolyLine2, plateThickness: number): number {
  const dx = edge[2] - edge[0];
  const dy = edge[3] - edge[1];
  if (Math.abs(dx) < DIST_EPSILON) {
    return 0;
  }
  return plateThickness * Math.abs(dy / dx);
}

/**
 * Clip planes for balloon-framing sloped top plates: vertical end clips at corner studs and a
 * vertical peak clip oriented so each plate keeps its own slope segment.
 */
export function buildSlopedTopPlateClipPlanes(
  edgeIndex: number,
  perimeter: FramingBoundaryWithMetadata,
  elevation: number,
  plateThickness: number,
): SlopedTopPlateClipResult {
  const pl = perimeter.polyline;
  const edge = polyLine2Edge(pl, edgeIndex);
  if (classifyBoundaryEdgeType(pl, edgeIndex) !== WallFramingType.TOP_PLATE || !isSlopedTopPlateEdge(edge)) {
    return { clipPlanes: [], startExtension: 0, endExtension: 0 };
  }

  const startX = edge[0];
  const startY = edge[1];
  const endX = edge[2];
  const endY = edge[3];
  const midX = (startX + endX) / 2;
  let peakX = midX;
  if (isPeakVertex(pl, startX, startY)) {
    peakX = startX;
  } else if (isPeakVertex(pl, endX, endY)) {
    peakX = endX;
  }

  const planes: Plane[] = [];
  let startExtension = 0;
  let endExtension = 0;
  const slopeExtension = slopeExtensionAlongEdge(edge, plateThickness);

  const addClipAtVertex = (x: number, y: number, isStart: boolean): void => {
    if (isPeakVertex(pl, x, y)) {
      planes.push(verticalClipPlaneAtLayoutX(x, midX > x, elevation));
      return;
    }
    if (isCornerVertex(pl, x, y)) {
      planes.push(verticalClipPlaneAtLayoutX(x, x < peakX, elevation));
      if (isStart) {
        startExtension = slopeExtension;
      } else {
        endExtension = slopeExtension;
      }
    }
  };

  addClipAtVertex(startX, startY, true);
  addClipAtVertex(endX, endY, false);

  return { clipPlanes: planes, startExtension, endExtension };
}
